import { EmployeeShift } from '../models/employee.model';

/**
 * When each shift runs, in the plant's local time (hours from midnight; an end
 * before its start runs past midnight). Used to decide who is "on" now, and
 * when a session nobody closed should be closed for them.
 *
 * PLACEHOLDER HOURS - set these to the real shift times.
 */
export const FINISHING_LABOR_SHIFT_WINDOWS: Record<EmployeeShift, { start: number; end: number }> = {
    [EmployeeShift.First]: { start: 6, end: 14.5 },
    [EmployeeShift.Second]: { start: 14.5, end: 23 },
    [EmployeeShift.Third]: { start: 23, end: 6 },
};

/** grace after a shift's end before an open session is closed for the person */
export const FINISHING_LABOR_SHIFT_GRACE_HOURS = 0.5;

/** the cap for someone with no shift on record - a session is never left open longer */
export const FINISHING_LABOR_MAX_SESSION_HOURS = 12;

const HOUR = 60 * 60 * 1000;

/** the shift's occurrence that starts on the calendar day of `day` */
function occurrence(day: Date, window: { start: number; end: number }): { start: Date; end: Date } {
    const midnight = new Date(day.getFullYear(), day.getMonth(), day.getDate());
    const start = new Date(midnight.getTime() + window.start * HOUR);
    const endOffset = window.end > window.start ? window.end : window.end + 24;
    return { start, end: new Date(midnight.getTime() + endOffset * HOUR) };
}

/** whether `when` falls inside the shift, with `graceHours` either side */
export function isWithinFinishingShift(when: Date, shift: EmployeeShift, graceHours = 1): boolean {
    const window = FINISHING_LABOR_SHIFT_WINDOWS[shift];
    const yesterday = new Date(when.getFullYear(), when.getMonth(), when.getDate() - 1);
    return [occurrence(yesterday, window), occurrence(when, window)].some(({ start, end }) =>
        when.getTime() >= start.getTime() - graceHours * HOUR && when.getTime() <= end.getTime() + graceHours * HOUR);
}

/**
 * When a session that started at `startedOn` should be closed if nobody closes
 * it: the end of the shift it started in (or the next one, for an early
 * start), plus the grace. Someone with no shift gets the flat cap.
 */
export function finishingLaborSessionCutoff(startedOn: Date, shift: EmployeeShift | null): Date {
    const cap = new Date(startedOn.getTime() + FINISHING_LABOR_MAX_SESSION_HOURS * HOUR);
    if (!shift) {
        return cap;
    }
    const window = FINISHING_LABOR_SHIFT_WINDOWS[shift];
    const yesterday = new Date(startedOn.getFullYear(), startedOn.getMonth(), startedOn.getDate() - 1);
    const candidates = [occurrence(yesterday, window), occurrence(startedOn, window)];
    const containing = candidates.find(({ start, end }) => startedOn >= start && startedOn <= end);
    const shiftEnd = containing?.end
        ?? candidates.map(({ end }) => end).find(end => end > startedOn)
        ?? occurrence(new Date(startedOn.getTime() + 24 * HOUR), window).end;
    const cutoff = new Date(shiftEnd.getTime() + FINISHING_LABOR_SHIFT_GRACE_HOURS * HOUR);
    return cutoff < cap ? cutoff : cap;
}
